import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { loginDestination } from "@/lib/loginDestination";
import { registrationLink, registrationReturnPath } from "@/lib/registrationIntent";
import { SERVICE_OPERATOR, SUPPORT_PHONE, SUPPORT_PHONE_RAW } from "@/lib/contact";
import Logo from "@/components/Logo";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const goAfter = (role) => {
    // Phase 44.31.1 — supporta anche ?next= (settato dal logout 401 automatico)
    let nextParam = null;
    try {
      const sp = new URLSearchParams(location.search);
      nextParam = sp.get("next");
    } catch { /* noop */ }
    const from = loginDestination(role, [nextParam, location.state?.from?.pathname, registrationReturnPath(location.search, role)], window.location.origin);
    navigate(from, { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const res = await login(email, password);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    toast.success(`Benvenuto, ${res.user.name}`);
    goAfter(res.user.role);
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="relative hidden min-w-0 w-full lg:block bg-dapnow-ink overflow-hidden">
        <div
          className="absolute inset-0 map-bg opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-dapnow-blue/30 via-transparent to-dapnow-red/20" />
        <div className="relative h-full p-10 flex flex-col justify-between">
          <Logo variant="light" size="xl" />
          <div>
            <p className="overline text-white/70">DapNow</p>
            <h2 className="mt-2 font-display text-4xl font-extrabold tracking-tighter text-white max-w-md leading-tight">
              Bentornato. <br /> Le tue consegne ti aspettano.
            </h2>
            <p className="mt-3 text-white/90 max-w-sm text-sm font-medium">
              Consegne in giornata in Ticino e nei Grigioni italiano, con driver interni e secondo disponibilità.
            </p>
          </div>
        </div>
      </div>

      <div className="flex min-w-0 w-full max-w-full items-center justify-center px-6 py-10">
        <div className="min-w-0 w-full max-w-sm">
          <div className="lg:hidden min-w-0 w-full max-w-full mb-10 pt-[env(safe-area-inset-top)]">
            <Logo size="xl" />
          </div>
          <p className="overline">Accedi</p>
          <h1 className="font-display text-4xl font-extrabold tracking-tighter mt-1">Bentornato</h1>
          <p className="text-sm text-dapnow-muted mt-2">Accedi al tuo account DapNow.</p>

          <form className="mt-8 space-y-4" onSubmit={onSubmit}>
            <div>
              <label className="text-sm font-semibold tracking-tight">Email</label>
              <input
                type="email"
                required
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.ch"
                className="input-tall mt-1.5 w-full"
              />
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <label className="text-sm font-semibold tracking-tight">Password</label>
                <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs font-semibold text-dapnow-blue hover:underline">
                  Hai dimenticato la password?
                </Link>
              </div>
              <input
                type="password"
                required
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="input-tall mt-1.5 w-full"
              />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-dapnow-red">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-btn"
              className="btn-primary w-full"
            >
              {submitting ? "Accesso…" : "Accedi"}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </button>
          </form>

          <p className="mt-6 text-sm text-dapnow-muted">
            Non hai un account?{" "}
            <Link to={registrationLink(location.search)} data-testid="login-register-link" className="font-semibold text-dapnow-blue hover:underline">
              Registrati
            </Link>
          </p>

          {/* Phase 44.15 — Trust: link supporto reali (mail + WhatsApp) sempre
              visibili sulla Login. Risolve l'issue in cui i messaggi di errore
              dicevano "contatta supporto" senza un canale realmente cliccabile. */}
          <div className="mt-8 pt-6 border-t border-dapnow-line/60">
            <p className="text-xs text-dapnow-muted mb-3">{SERVICE_OPERATOR}. <Link to="/legal" className="underline">Gestore e contatti</Link></p>
            <p className="text-xs text-dapnow-muted">Hai bisogno d&apos;aiuto?</p>
            <div className="mt-2 flex flex-col sm:flex-row gap-2 sm:gap-4">
              <a
                href="mailto:support@dap-now.ch?subject=Aiuto%20accesso%20DapNow"
                data-testid="login-support-email"
                className="text-sm font-semibold text-dapnow-blue hover:underline"
              >
                support@dap-now.ch
              </a>
              <a
                href={`tel:+${SUPPORT_PHONE_RAW}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="login-support-phone"
                className="text-sm font-semibold text-emerald-700 hover:underline"
              >
                Chiama {SUPPORT_PHONE}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
